import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import useCanonicalUrl from '../hooks/useCanonicalUrl'
import HomeTemplate from '../templates/HomeTemplate'
import { Code, Zap, Users, Globe, Target, Heart, Shield, Lightbulb, Award, MessageCircle, HelpCircle, CheckCircle, Star, ArrowRight } from 'lucide-react'
import './AboutUs.css'

const AboutUs = () => {
  useCanonicalUrl();
  useEffect(() => {
    document.title = 'About Us - EveryKits';

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', 'EveryKits is a small independent team building free, no-signup online tools that run right in your browser. Here is who we are and why we built it.')
    }
  }, []);

  const teamMembers = [
    {
      name: "Ehtsham Arshad",
      role: "MERN Stack Developer",
      bio: "Full-stack developer specializing in the MERN stack (MongoDB, Express, React, Node.js). Builds and maintains the tools on EveryKits with a focus on speed, reliability, and clean client-side processing so user data never leaves the browser.",
      image: "/team/member1.jpg"
    },
    {
      name: "Sultan Ali",
      role: "MERN Stack Developer",
      bio: "Full-stack MERN developer focused on building intuitive, responsive interfaces and robust features. Turns ideas into fast, accessible tools that work seamlessly across every device without signups or paywalls.",
      image: "/team/member2.jpg"
    },
    {
      name: "Muhammad Usman",
      role: "SEO Specialist",
      bio: "Handles search engine optimization and content strategy for EveryKits — from keyword research and on-page SEO to structured data and AI-search visibility, helping more people discover the free tools they need.",
      image: "/team/member3.jpg"
    }
  ];

  const faqs = [
    {
      question: "Are EveryKits tools really free to use?",
      answer: "Yes, absolutely! All our tools are completely free with no hidden charges, premium features, or subscription requirements. We believe in democratizing access to essential digital utilities."
    },
    {
      question: "How do you keep the tools free?",
      answer: "We're funded through minimal, non-intrusive advertising and partnerships with companies who share our vision. This allows us to maintain and improve our tools without charging users."
    },
    {
      question: "Is my data safe and private?",
      answer: "Privacy is our top priority. Most of our tools process data entirely in your browser using client-side JavaScript. We don't store your files, track your inputs, or collect personal information."
    },
    {
      question: "Do I need to create an account?",
      answer: "No account required! You can start using any of our tools instantly without registration, email addresses, or complex sign-up processes. Just visit and use."
    },
    {
      question: "Can I use EveryKits on mobile devices?",
      answer: "Absolutely! EveryKits is fully responsive and optimized for all devices - smartphones, tablets, and desktop computers. Use our tools anywhere, anytime."
    },
    {
      question: "How accurate are your tools?",
      answer: "Our tools are built with precision and reliability in mind. We use industry-standard algorithms and regularly test our tools to ensure accurate, professional-grade results."
    },
    {
      question: "Can I suggest new tools or features?",
      answer: "We love feedback! You can reach out through our contact page with suggestions for new tools or improvements. We're always looking to expand our utility belt based on user needs."
    },
    {
      question: "Do you offer API access for developers?",
      answer: "Currently, our tools are designed for direct use through our web interface. We're exploring API options for the future. Stay tuned for updates!"
    },
    {
      question: "How often do you add new tools?",
      answer: "We're constantly working on improvements and new additions. Major updates typically happen monthly, with smaller improvements and bug fixes rolled out regularly."
    },
    {
      question: "Can I use EveryKits for commercial projects?",
      answer: "Yes! Our tools are free for both personal and commercial use. Whether you're a student, professional, or business, you can use EveryKits without restrictions."
    }
  ];

  return (
    <HomeTemplate>
      <div className="about-us">
        {/* Hero Section */}
        <section className="about-hero">
          <h1>About EveryKits</h1>
          <p className="subtitle">Free online tools that just work — no signups, no paywalls.</p>
          <p className="hero-description">
            EveryKits is a small collection of everyday web tools built by an independent team. Everything runs free in your browser, with no account and no hidden costs.
          </p>
        </section>

        {/* Company Story & Mission */}
        <section className="company-story">
          <div className="story-content">
            <h2>Our Story & Mission</h2>
            <p>
              EveryKits started with a simple annoyance. Every time we needed a quick tool online — to resize an image, generate a QR code, or count words — we ran into the same walls: sign-up forms, subscription prompts, watermarks, or pages so buried in ads that the actual tool was hard to find.
            </p>
            <p>
              We're a small team — two developers and one person handling SEO and content — so we figured we'd just build the tools we wished existed. Simple, fast, and genuinely free, with no account required and no catch.
            </p>
            <p>
              That's really all EveryKits is: a growing set of free online tools that run right in your browser. Most of them do all the work on your own device, which means your files and text aren't uploaded to a server. We add and improve tools as we go, usually based on what we ourselves need or what people ask for.
            </p>
            <p>
              We're still early and still growing. If one of our tools saved you a few minutes or a subscription fee, that's exactly what we were hoping for — and we'd genuinely like to hear what we should build next.
            </p>
          </div>
        </section>

        {/* Why We Built This */}
        <section className="why-built">
          <h2>Why We Built EveryKits</h2>
          <div className="why-grid">
            <div className="why-item">
              <Target className="why-icon" />
              <h3>Tools we actually use</h3>
              <p>We build the tools we reach for ourselves — resizing an image, generating a QR code, checking a word count. If it saves us a few minutes, chances are it saves you some too.</p>
            </div>
            <div className="why-item">
              <Shield className="why-icon" />
              <h3>Your data stays with you</h3>
              <p>Most of our tools run entirely in your browser, so your files and text are processed on your own device and never uploaded to a server. Less for us to store, more privacy for you.</p>
            </div>
            <div className="why-item">
              <Heart className="why-icon" />
              <h3>Free, with no catch</h3>
              <p>No paywall, no trial that expires, no "upgrade to unlock." The tools are free to use, and you don't need to hand over an email or create an account to get started.</p>
            </div>
            <div className="why-item">
              <Lightbulb className="why-icon" />
              <h3>Simple to use</h3>
              <p>You shouldn't need a tutorial to resize an image. We try to keep each tool to the point — open the page, do the thing, move on with your day.</p>
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="team-section">
          <h2>Meet Our Team</h2>
          <p className="team-intro">We're a small team of three — two developers who build and maintain the tools, and one who handles SEO and content.</p>          <div className="team-grid">
            {teamMembers.map((member, index) => (
              <div key={index} className="team-card">
                {member.image ? (
                  <img
                    className="team-avatar"
                    src={member.image}
                    alt={member.name}
                    loading="lazy"
                    onError={(e) => {
                      // agar image na mile to initials-circle dikha do
                      e.target.style.display = 'none'
                      e.target.nextSibling.style.display = 'flex'
                    }}
                  />
                ) : null}
                <div
                  className="team-avatar"
                  style={{
                    display: member.image ? 'none' : 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.8rem',
                    fontWeight: 700,
                    color: '#fff',
                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                    textTransform: 'uppercase',
                  }}
                  aria-hidden="true"
                >
                  {member.name
                    .split(' ')
                    .map((word) => word[0])
                    .slice(0, 2)
                    .join('')}
                </div>
                <div className="team-info">
                  <h3>{member.name}</h3>
                  <p className="team-role">{member.role}</p>
                  <p className="team-bio">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Our Values */}
        <section className="values-section">
          <h2>Our Values</h2>
          <div className="values-grid">
            <div className="value-item">
              <Award className="value-icon" />
              <h3>Quality</h3>
              <p>We test each tool before it goes live and fix things when they break. If a tool gives you a wrong result, that's a bug we want to hear about.</p>
            </div>
            <div className="value-item">
              <Users className="value-icon" />
              <h3>Works everywhere</h3>
              <p>Our tools work on phones, tablets, and desktops without any install. If you have a browser and internet, you're set.</p>
            </div>
            <div className="value-item">
              <Shield className="value-icon" />
              <h3>Privacy</h3>
              <p>We collect as little as possible. Most tools never send your data anywhere, and we're not in the business of tracking or reselling anything.</p>
            </div>
            <div className="value-item">
              <Zap className="value-icon" />
              <h3>Speed</h3>
              <p>Because the work happens in your browser, there's no upload wait or server queue. Most tools give you a result the moment you hit go.</p>
            </div>
            <div className="value-item">
              <Heart className="value-icon" />
              <h3>Free to use</h3>
              <p>The tools are free and we intend to keep them that way. A few unobtrusive ads cover our costs so we don't have to charge you.</p>
            </div>
            <div className="value-item">
              <Code className="value-icon" />
              <h3>Always improving</h3>
              <p>We add new tools and refine existing ones over time, usually based on what we need ourselves or what people ask us for.</p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="faq-section">
          <h2>Frequently Asked Questions</h2>
          <p className="faq-intro">A few things people often ask us.</p>
          <div className="faq-grid">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-item">
                <div className="faq-question">
                  <HelpCircle className="faq-icon" />
                  <h3>{faq.question}</h3>
                </div>
                <div className="faq-answer">
                  <p>{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Call to Action */}
        <section className="cta-section">
          <div className="cta-content">
            <h2>Give the tools a try</h2>
            <p>Every tool is free and works right away — no account, no install. Browse what we've built so far, and tell us what we should add next.</p>
            <div className="cta-buttons">
              <Link to="/" className="cta-btn primary">
                <Star className="btn-icon" />
                Explore All Tools
                <ArrowRight className="btn-arrow" />
              </Link>
              <Link to="/contact/" className="cta-btn secondary">
                <MessageCircle className="btn-icon" />
                Contact Us
              </Link>
            </div>
          </div>
        </section>

        {/* Future Vision */}
        {/* Future Vision */}
        <section className="future-vision">
          <h2>What's next</h2>
          <p>
            We keep a running list of tools we want to build and rough edges we want to fix. It grows mostly from our own day-to-day needs and from suggestions people send us.
          </p>
          <p>
            A few things on that list right now: more file and image tools, better mobile layouts, and small quality-of-life improvements to the tools we already have. We'd rather ship a few solid tools than a long list of half-working ones.
          </p>
          <p>
            If there's something you keep wishing existed, <Link to="/contact/">let us know</Link> — a lot of what we build starts as someone's request.
          </p>
        </section>
      </div>
    </HomeTemplate>
  )
}

export default AboutUs
